import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import { calculateNights } from '../utils/nightsCalculation';
import { buildCacheKey, saveHotelDetail } from '../utils/hotelDetailStorage';
import type { HotelOfferCardModel, RateFeature } from '../types/hotels.presentation';
import { formatHotelPrice } from '../utils/formatHotelPrice';
import { HotelImagePlaceholder } from './HotelImagePlaceholder';

const STAR_SYMBOL = '★';
const SUMMARY_SEPARATOR = ' · ';

/** Translates a rate feature label: i18n keys get translated, raw text stays as-is. */
function translateFeatureLabel(feature: RateFeature, t: (key: string) => string): string {
  return feature.label.startsWith('hotels.') ? t(feature.label) : feature.label;
}

/** Maps rate feature type to a Tailwind color class for chip styling.
 *  Refundability branches emerald (refundable) vs amber (non-refundable). */
function chipColors(
  type: RateFeature['type'],
  isRefundable = false,
): { bg: string; text: string; ring: string } {
  switch (type) {
    case 'room':
      return { bg: 'bg-blue-50', text: 'text-blue-800', ring: 'ring-blue-200' };
    case 'meal':
      return { bg: 'bg-green-50', text: 'text-green-800', ring: 'ring-green-200' };
    case 'refundability':
      return isRefundable
        ? { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' }
        : { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' };
    default:
      return { bg: 'bg-gray-50', text: 'text-gray-700', ring: 'ring-gray-200' };
  }
}

/** Chip span rendered as an inline tag badge. */
function FeatureChip({
  children,
  bg,
  text,
  ring,
}: {
  children: React.ReactNode;
  bg: string;
  text: string;
  ring: string;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${bg} ${text} ring-1 ring-inset ${ring}`}
    >
      {children}
    </span>
  );
}

interface HotelOfferCardProps {
  offer: HotelOfferCardModel;
  checkIn?: string;
  checkOut?: string;
  adults?: number;
  childrenCount?: number;
}

export function HotelOfferCard({
  offer,
  checkIn,
  checkOut,
  adults,
  childrenCount,
}: HotelOfferCardProps) {
  const { t, i18n } = useTranslation();
  const { background, text } = useListingColors();
  const nights = calculateNights(checkIn, checkOut);
  const [imageError, setImageError] = useState(false);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  // ---- Structured body ----

  const rateFeatureList = useMemo(() => offer.rateFeatures ?? [], [offer.rateFeatures]);
  const mealFeatures = useMemo(
    () => rateFeatureList.filter((f) => f.type === 'meal'),
    [rateFeatureList],
  );

  const hasStructuredData =
    mealFeatures.length > 0 || offer.roomType != null || (offer.amenities?.length ?? 0) > 0;

  const structuredBody: React.ReactNode = hasStructuredData ? (
    <div className='mt-2 space-y-1.5'>
      {offer.roomType ? (
        <p className='flex flex-wrap items-center gap-x-1.5 text-xs'>
          <span className='text-gray-500'>{`${t('hotels.card.roomType')}: `}</span>
          <FeatureChip bg='bg-blue-50' ring='ring-blue-200' text='text-blue-800'>
            {offer.roomType}
          </FeatureChip>
        </p>
      ) : null}
      {mealFeatures.map((f, i) => {
        const colors = chipColors('meal');
        return (
          <p className='flex flex-wrap items-center gap-x-1.5 text-xs' key={`meal-${i}`}>
            <span className='text-gray-500'>{`${t('hotels.detail.board')}: `}</span>
            <FeatureChip bg={colors.bg} ring={colors.ring} text={colors.text}>
              {translateFeatureLabel(f, t)}
            </FeatureChip>
          </p>
        );
      })}
      {offer.amenities?.length ? (
        <p className='text-xs'>
          <span className='text-gray-500'>{`${t('hotels.card.amenities')}: `}</span>
          {offer.amenities.join(', ')}
        </p>
      ) : null}
    </div>
  ) : null;

  const promotionBadge = offer.promotionLabel ? (
    <div className='mt-2'>
      <span className='inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-700 ring-1 ring-inset ring-orange-200'>
        {offer.promotionLabel}
      </span>
    </div>
  ) : null;

  const rateSummaryFallback: React.ReactNode = !hasStructuredData ? (
    <p className='mt-2 rounded-md bg-gray-50 px-3 py-2 text-sm leading-relaxed text-gray-700 ring-1 ring-gray-100'>
      {offer.rateSummary
        .split(SUMMARY_SEPARATOR)
        .map((part) => (part.startsWith('hotels.') ? t(part) : part))
        .join(SUMMARY_SEPARATOR)}
    </p>
  ) : null;

  const cacheKey = offer.detail ? buildCacheKey(offer.detail) : '';

  // Persist detail on any pointer interaction with the link (covers left,
  // middle and right click before navigation) plus onClick for keyboard.
  // Saving on render for every card overflows the localStorage quota.
  const persistDetail = () => {
    if (offer.detail) saveHotelDetail(offer.detail);
  };

  const urlParams = new URLSearchParams();
  if (checkIn) urlParams.set('checkIn', checkIn);
  if (checkOut) urlParams.set('checkOut', checkOut);
  if (cacheKey) urlParams.set('cacheKey', cacheKey);
  if (adults != null) urlParams.set('adults', String(adults));
  if (childrenCount != null) urlParams.set('children', String(childrenCount));
  const queryString = urlParams.toString();
  const detailUrl = queryString
    ? `/buscarxhoteles/detalle/${offer.hotelId}?${queryString}`
    : `/buscarxhoteles/detalle/${offer.hotelId}`;

  return (
    <article className='flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm'>
      {/* Hotel image */}
      {offer.imageUrl && !imageError ? (
        <div className='aspect-[16/10] w-full overflow-hidden bg-gray-100'>
          <img
            alt=''
            aria-hidden='true'
            className='h-full w-full object-cover'
            loading='lazy'
            onError={handleImageError}
            src={offer.imageUrl}
          />
        </div>
      ) : (
        <HotelImagePlaceholder label={t('hotels.detail.imageUnavailable')} />
      )}

      <div className='relative flex flex-1 flex-col p-4'>
        <div className='flex flex-1 flex-col gap-4'>
          <div className='min-w-0 flex-1'>
            <div className='pr-14'>
              <h3
                className='overflow-hidden text-lg font-semibold leading-snug text-gray-900'
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                }}
              >
                {offer.hotelName}
              </h3>
              {offer.stars ? (
                <span className='absolute right-4 top-4 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-100'>
                  {offer.stars}
                  {STAR_SYMBOL}
                </span>
              ) : null}
            </div>

            {offer.address ? (
              <p className='mt-1 text-xs font-medium text-gray-500'>{offer.address}</p>
            ) : null}

            {structuredBody}
            {promotionBadge}
            {rateSummaryFallback}
          </div>

          <aside className='mt-auto flex flex-col gap-2 border-t border-gray-100 pt-4'>
            <span className='text-xs uppercase tracking-wide text-gray-500'>
              {t('hotels.results.priceLegend')}
            </span>
            <div className='flex items-center justify-between gap-4 min-[1180px]:flex-col min-[1180px]:items-stretch'>
              <p className='text-xl font-bold text-gray-950'>
                {formatHotelPrice(offer.totalPrice, offer.currency, i18n?.language) ??
                  t('hotels.results.rateUnavailable')}
                {nights != null ? (
                  <span className='ml-2 text-sm font-normal text-gray-500'>
                    / {nights} {t(nights === 1 ? 'hotels.night' : 'hotels.nights')}
                  </span>
                ) : null}
              </p>
              <a
                aria-label={`${t('hotels.results.viewDetail')} ${offer.hotelName}`}
                className='shrink-0 rounded-md px-3 py-2 text-center text-sm font-semibold transition hover:brightness-95 min-[1180px]:w-full'
                href={detailUrl}
                onClick={persistDetail}
                onPointerDown={persistDetail}
                rel='noopener noreferrer'
                style={{ backgroundColor: background, color: text }}
                target='_blank'
              >
                {t('hotels.results.viewDetail')}
              </a>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}
